/**
 * Production Trend Service - Enterprise Command Center
 * 
 * Fetches real production data from Firestore to generate
 * sparkline trends and analytics for the map interface.
 */

import { db } from '../firebase/client';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';

export interface ProductionDataPoint {
    date: Date;
    oil: number;      // BBL
    gas: number;      // MCF
    water: number;    // BBL
}

export interface ProductionTrend {
    wellId: string;
    dataPoints: ProductionDataPoint[];
    avgOil: number;
    avgGas: number;
    avgWater: number;
    trend: 'up' | 'down' | 'stable';
    percentChange: number;
    lastUpdated: Date | null;
}

export interface TankLevel {
    wellId: string;
    tankId: string;
    tankName: string;
    type: 'oil' | 'water' | 'slop';
    capacity: number;     // BBL
    currentLevel: number; // BBL
    percentage: number;
    lastGauged: Date | null;
    daysUntilFull: number | null;
}

class ProductionTrendService {
    private trendCache: Map<string, { data: ProductionTrend; expires: number }> = new Map();
    private tankCache: Map<string, { data: TankLevel[]; expires: number }> = new Map();
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    /**
     * Fetch production trend for a single well (last 7 days)
     */
    async getWellTrend(wellId: string, companyId: string): Promise<ProductionTrend> {
        const cacheKey = `${companyId}:${wellId}`;

        // Check cache
        const cached = this.trendCache.get(cacheKey);
        if (cached && cached.expires > Date.now()) {
            return cached.data;
        }

        try {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            // Query production entries from well subcollection
            // Path: companies/{companyId}/wells/{wellId}/production_entries
            const entriesRef = collection(db, 'companies', companyId, 'wells', wellId, 'production_entries');
            const q = query(
                entriesRef,
                where('date', '>=', Timestamp.fromDate(sevenDaysAgo)),
                orderBy('date', 'desc'),
                limit(7)
            );

            const snapshot = await getDocs(q);
            const dataPoints: ProductionDataPoint[] = [];

            snapshot.forEach(doc => {
                const data = doc.data();
                dataPoints.push({
                    date: data.date?.toDate?.() || new Date(data.date),
                    oil: data.oilProduction || data.oil || 0,
                    gas: data.gasProduction || data.gas || 0,
                    water: data.waterProduction || data.water || 0
                });
            });

            // Calculate averages and trends
            const avgOil = dataPoints.length > 0
                ? dataPoints.reduce((sum, dp) => sum + dp.oil, 0) / dataPoints.length
                : 0;
            const avgGas = dataPoints.length > 0
                ? dataPoints.reduce((sum, dp) => sum + dp.gas, 0) / dataPoints.length
                : 0;
            const avgWater = dataPoints.length > 0
                ? dataPoints.reduce((sum, dp) => sum + dp.water, 0) / dataPoints.length
                : 0;

            // Calculate trend (compare first half to second half)
            let trend: 'up' | 'down' | 'stable' = 'stable';
            let percentChange = 0;

            if (dataPoints.length >= 2) {
                const midpoint = Math.floor(dataPoints.length / 2);
                const recentAvg = dataPoints.slice(0, midpoint).reduce((sum, dp) => sum + dp.oil, 0) / midpoint || 0;
                const olderAvg = dataPoints.slice(midpoint).reduce((sum, dp) => sum + dp.oil, 0) / (dataPoints.length - midpoint) || 0;

                if (olderAvg > 0) {
                    percentChange = ((recentAvg - olderAvg) / olderAvg) * 100;
                    if (percentChange > 5) trend = 'up';
                    else if (percentChange < -5) trend = 'down';
                }
            }

            const result: ProductionTrend = {
                wellId,
                dataPoints: dataPoints.reverse(), // Chronological order for sparkline
                avgOil: Math.round(avgOil),
                avgGas: Math.round(avgGas),
                avgWater: Math.round(avgWater),
                trend,
                percentChange: Math.round(percentChange),
                lastUpdated: dataPoints.length > 0 ? dataPoints[dataPoints.length - 1].date : null
            };

            // Cache result
            this.trendCache.set(cacheKey, {
                data: result,
                expires: Date.now() + this.CACHE_DURATION
            });

            return result;

        } catch (error) {
            console.error(`Failed to fetch trend for well ${wellId}:`, error);
            // Return empty trend on error
            return {
                wellId,
                dataPoints: [],
                avgOil: 0,
                avgGas: 0,
                avgWater: 0,
                trend: 'stable',
                percentChange: 0,
                lastUpdated: null
            };
        }
    }

    /**
     * Fetch trends for multiple wells (batch optimization)
     */
    async getWellTrends(wellIds: string[], companyId: string): Promise<Map<string, ProductionTrend>> {
        const results = new Map<string, ProductionTrend>();

        // Process in parallel with rate limiting
        const batchSize = 10;
        for (let i = 0; i < wellIds.length; i += batchSize) {
            const batch = wellIds.slice(i, i + batchSize);
            const promises = batch.map(async wellId => {
                const trend = await this.getWellTrend(wellId, companyId);
                results.set(wellId, trend);
            });
            await Promise.all(promises);
        }

        return results;
    }

    /**
     * Get tank levels for a well
     * Note: Currently coded for future data - will return mock data if no real data exists
     */
    async getTankLevels(wellId: string, companyId: string): Promise<TankLevel[]> {
        const cacheKey = `tanks:${companyId}:${wellId}`;

        // Check cache
        const cached = this.tankCache.get(cacheKey);
        if (cached && cached.expires > Date.now()) {
            return cached.data;
        }

        try {
            // Query tank readings from Firestore
            const tanksRef = collection(db, 'companies', companyId, 'tankReadings');
            const q = query(
                tanksRef,
                where('wellId', '==', wellId),
                orderBy('readingDate', 'desc'),
                limit(10) // Get latest readings for each tank
            );

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                // No tank data yet - return empty array
                // The UI will handle showing "No tank data" appropriately
                return [];
            }

            const tankMap = new Map<string, TankLevel>();

            snapshot.forEach(doc => {
                const data = doc.data();
                const tankId = data.tankId;

                // Only keep the most recent reading for each tank
                if (!tankMap.has(tankId)) {
                    const capacity = data.capacity || 500;
                    const currentLevel = data.currentLevel || data.level || 0;
                    const percentage = Math.min(100, Math.round((currentLevel / capacity) * 100));

                    // Calculate days until full based on recent fill rate
                    const dailyIncrease = data.dailyIncrease || data.fillRate || null;
                    const remainingCapacity = capacity - currentLevel;
                    const daysUntilFull = dailyIncrease && dailyIncrease > 0
                        ? Math.ceil(remainingCapacity / dailyIncrease)
                        : null;

                    tankMap.set(tankId, {
                        wellId,
                        tankId,
                        tankName: data.tankName || `Tank ${tankId}`,
                        type: data.type || 'oil',
                        capacity,
                        currentLevel,
                        percentage,
                        lastGauged: data.readingDate?.toDate?.() || new Date(data.readingDate),
                        daysUntilFull
                    });
                }
            });

            const results = Array.from(tankMap.values());

            // Cache results
            this.tankCache.set(cacheKey, {
                data: results,
                expires: Date.now() + this.CACHE_DURATION
            });

            return results;

        } catch (error) {
            console.error(`Failed to fetch tank levels for well ${wellId}:`, error);
            return [];
        }
    }

    /**
     * Get last visit timestamp for a well (from production entries or field visits)
     */
    async getLastVisit(wellId: string, companyId: string): Promise<Date | null> {
        try {
            // Check production entries from well subcollection
            // Path: companies/{companyId}/wells/{wellId}/production_entries
            const entriesRef = collection(db, 'companies', companyId, 'wells', wellId, 'production_entries');
            const q = query(
                entriesRef,
                orderBy('date', 'desc'),
                limit(1)
            );

            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                const data = snapshot.docs[0].data();
                return data.date?.toDate?.() || new Date(data.date);
            }

            return null;

        } catch (error) {
            console.error(`Failed to fetch last visit for well ${wellId}:`, error);
            return null;
        }
    }

    /**
     * Calculate revenue estimate based on production and current oil prices
     * Uses approximate WTI price (could be enhanced with real API)
     */
    calculateDailyRevenue(oilBbl: number, gasMcf: number, oilPrice: number = 72, gasPrice: number = 2.50): number {
        return Math.round((oilBbl * oilPrice) + (gasMcf * gasPrice));
    }

    /**
     * Clear caches
     */
    clearCache(): void {
        this.trendCache.clear();
        this.tankCache.clear();
    }
}

export const productionTrendService = new ProductionTrendService();
